import * as THREE from 'three';
import { SCATTER } from '../../config/tuning';
import { SimplexNoise } from './Noise';

/**
 * Vegetation geometry (SPEC §5.1): a trunk cylinder plus crossed alpha-tested canopy quads,
 * falling back to a billboard beyond 800 m.
 *
 * Three levels, and the drop between them is aggressive on purpose. A tree is a few hundred
 * pixels for a fraction of a second at 90 m/s; what has to survive the distance is the *mass and
 * colour* of the canopy, not its silhouette, so LOD 1 halves the crossed quads and LOD 2 is a
 * single camera-facing quad. Spending triangles on a shape nobody can resolve is how a scatter
 * system eats a frame budget.
 */

/** LOD 0: trunk plus four crossed canopy quads. LOD 1: two quads. LOD 2: a single billboard. */
export function createTreeGeometry(lod: number): THREE.BufferGeometry {
  const heightM = SCATTER.treeHeightM;
  const canopyWidth = heightM * 0.62;
  const canopyBase = heightM * 0.32;

  if (lod >= 2) {
    // Billboard: one quad, rotated to face the camera by the shader's Y-axis alignment.
    const geometry = new THREE.PlaneGeometry(canopyWidth, heightM * 0.8);
    geometry.translate(0, heightM * 0.45, 0);
    return geometry;
  }

  const quads = lod === 0 ? 4 : 2;
  const parts: THREE.BufferGeometry[] = [];

  if (lod === 0) {
    const trunk = new THREE.CylinderGeometry(
      heightM * 0.022,
      heightM * 0.04,
      canopyBase * 1.4,
      5,
      1,
    );
    trunk.translate(0, canopyBase * 0.7, 0);
    parts.push(trunk);
  }

  for (let i = 0; i < quads; i += 1) {
    const quad = new THREE.PlaneGeometry(canopyWidth, heightM * 0.78);
    quad.translate(0, canopyBase + heightM * 0.39, 0);
    quad.rotateY((i / quads) * Math.PI);
    parts.push(quad);
  }

  return mergeGeometries(parts);
}

/**
 * The canopy texture: a soft alpha-tested blob of foliage. Generated, like everything else.
 * Alpha testing rather than blending, so the trees need no sorting and no separate pass.
 */
export function createFoliageTexture(size = 128): THREE.DataTexture {
  const noise = new SimplexNoise(0xf0_11a6e);
  const data = new Uint8Array(size * size * 4);

  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const index = (y * size + x) * 4;
      const u = (x / size) * 2 - 1;
      const v = (y / size) * 2 - 1;

      // A blob, eroded by noise so the edge is ragged rather than elliptical.
      const radial = 1 - Math.min(1, Math.hypot(u * 1.15, v * 0.95));
      const erosion = noise.fbm(x * 0.06, y * 0.06, 4) * 0.5 + 0.5;
      const mask = radial * 1.35 - erosion * 0.55;

      const shade = 0.62 + erosion * 0.5;
      data[index] = Math.round(34 * shade);
      data[index + 1] = Math.round(62 * shade);
      data[index + 2] = Math.round(28 * shade);
      data[index + 3] = mask > 0.12 ? 255 : 0;
    }
  }

  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  return texture;
}

/**
 * Merge geometries by concatenating their attributes. Three ships a utility for this in its
 * addons; a dozen lines here keeps the dependency surface at exactly `three`.
 */
export function mergeGeometries(parts: readonly THREE.BufferGeometry[]): THREE.BufferGeometry {
  let vertexCount = 0;
  let indexCount = 0;
  for (const part of parts) {
    vertexCount += part.getAttribute('position').count;
    const index = part.getIndex();
    indexCount += index === null ? part.getAttribute('position').count : index.count;
  }

  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const uvs = new Float32Array(vertexCount * 2);
  const indices = new Uint16Array(indexCount);

  let vertexOffset = 0;
  let indexOffset = 0;
  for (const part of parts) {
    const position = part.getAttribute('position');
    const normal = part.getAttribute('normal');
    const uv = part.getAttribute('uv');
    const index = part.getIndex();

    positions.set(position.array as Float32Array, vertexOffset * 3);
    if (normal !== undefined) normals.set(normal.array as Float32Array, vertexOffset * 3);
    if (uv !== undefined) uvs.set(uv.array as Float32Array, vertexOffset * 2);

    if (index === null) {
      for (let i = 0; i < position.count; i += 1) indices[indexOffset + i] = vertexOffset + i;
      indexOffset += position.count;
    } else {
      for (let i = 0; i < index.count; i += 1) {
        indices[indexOffset + i] = vertexOffset + index.getX(i);
      }
      indexOffset += index.count;
    }
    vertexOffset += position.count;
    part.dispose();
  }

  const merged = new THREE.BufferGeometry();
  merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  merged.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  merged.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  merged.setIndex(new THREE.BufferAttribute(indices, 1));
  return merged;
}
